import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Allocation, ComponentToIssue, ProductionOrder } from '@ecinsa/shared';
import { ApiError, api } from '../lib/api';
import { useTerminal } from '../lib/store';
import { TopBar } from '../components/TopBar';
import { OrderPicker, PhasePicker, QuantityPad, SupervisorPin, parseQty } from '../components/pickers';
import { Dialog, Dot, Empty, Field, Pill } from '../components/ui';
import { qty } from '../lib/format';

type Step = 'order' | 'phase' | 'quantity' | 'components';

/**
 * Finalizar fase.
 *
 * Es la operación con más reglas del terminal: suma los tiempos de todos los
 * operarios que han pasado por la fase, emite la mano de obra de los recursos y
 * consume los componentes de emisión manual, con sus lotes, series y
 * ubicaciones. El paso de consumos reproduce el semáforo del terminal clásico:
 * gris si la línea no necesita detalle, rojo si falta por asignar, verde cuando
 * está completa.
 */
export function FinishPhase() {
  const { catalog, state, finishPhase, notify, busy } = useTerminal();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('order');
  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [phaseCode, setPhaseCode] = useState<string | null>(null);
  const [phases, setPhases] = useState<{ code: string; enabled: boolean; reason: string | null }[]>([]);
  const [quantity, setQuantity] = useState('');
  const [scrap, setScrap] = useState('');
  const [components, setComponents] = useState<ComponentToIssue[]>([]);
  const [editing, setEditing] = useState<ComponentToIssue | null>(null);
  const [askPin, setAskPin] = useState(false);
  const [loading, setLoading] = useState(false);

  const params = catalog?.parameters ?? {};
  const reservations = params['production.componentReservations'] === 'true';
  const askQuantity = params['production.requireQuantityOnFinish'] === 'true' || reservations;
  const captureScrap = params['quality.captureScrap'] === 'true';
  const phaseNames = useMemo(() => new Map((catalog?.phases ?? []).map((p) => [p.code, p.name])), [catalog]);

  useEffect(() => {
    if (!order) return;
    api
      .order(order.docEntry)
      .then((res) => setPhases(res.phases.map((p) => ({ ...p, enabled: true }))))
      .catch(() => setPhases(order.phases.map((code) => ({ code, enabled: true, reason: null }))));
  }, [order]);

  const loadComponents = useCallback(async () => {
    if (!order || !phaseCode) return;
    setLoading(true);
    try {
      const res = await api.components(order.docEntry, phaseCode, parseQty(quantity));
      setComponents(res.components);
    } catch (err) {
      notify({ kind: 'error', title: 'No se pudieron cargar los componentes', body: (err as Error).message });
      setComponents([]);
    } finally {
      setLoading(false);
    }
  }, [order, phaseCode, quantity, notify]);

  const goToComponents = async () => {
    await loadComponents();
    setStep('components');
  };

  const updateComponent = (lineNum: number, changes: Partial<ComponentToIssue>) => {
    setComponents((current) =>
      current.map((c) => {
        if (c.lineNum !== lineNum) return c;
        const next = { ...c, ...changes };
        const assigned = next.allocations.reduce((sum, a) => sum + a.quantity, 0);
        const needsDetail = next.managedBy !== 'none' || next.binEnabled;
        next.allocationStatus =
          !needsDetail || next.quantity <= 0 ? 'na' : Math.abs(assigned - next.quantity) < 1e-6 ? 'complete' : 'pending';
        return next;
      }),
    );
  };

  const blocking = components.find((c) => c.quantity > 0 && c.allocationStatus === 'pending');

  const submit = async (supervisorPin?: string) => {
    if (!order || !phaseCode) return;
    try {
      await finishPhase({
        orderDocEntry: order.docEntry,
        phaseCode,
        parentQuantity: parseQty(quantity),
        scrapQuantity: parseQty(scrap),
        scrapReasonCode: null,
        components: components
          .filter((c) => c.quantity > 0)
          .map((c) => ({ lineNum: c.lineNum, itemCode: c.itemCode, quantity: c.quantity, allocations: c.allocations })),
        ...(supervisorPin ? { supervisorPin } : {}),
      });
      notify({
        kind: 'ok',
        title: 'Fase finalizada',
        body: `OF ${order.docNum} · ${phaseNames.get(phaseCode) ?? phaseCode}. La emisión se está contabilizando en SAP.`,
      });
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_AUTHORIZED' && !supervisorPin) {
        setAskPin(true);
        return;
      }
      notify({ kind: 'error', title: 'No se pudo finalizar la fase', body: err instanceof ApiError ? err.message : String(err) });
    }
  };

  const back = () => {
    if (step === 'order') navigate('/');
    else if (step === 'phase') setStep('order');
    else if (step === 'quantity') setStep('phase');
    else setStep(askQuantity ? 'quantity' : 'phase');
  };

  return (
    <div className="app">
      <TopBar
        title="Finalizar fase"
        subtitle={order ? `OF ${order.docNum} · ${order.itemDescription}${phaseCode ? ` · ${phaseNames.get(phaseCode) ?? phaseCode}` : ''}` : 'Elige la orden de fabricación'}
        onBack={back}
      />

      <div className="content">
        {step === 'order' ? (
          <OrderPicker
            orders={catalog?.orders ?? []}
            manualEntry={params['terminal.manualEntry'] === 'true'}
            onPick={(o) => {
              setOrder(o);
              setStep('phase');
            }}
          />
        ) : null}

        {step === 'phase' ? (
          <PhasePicker
            phases={phases}
            names={phaseNames}
            onPick={(code) => {
              setPhaseCode(code);
              if (askQuantity) setStep('quantity');
              else void goToComponents();
            }}
          />
        ) : null}

        {step === 'quantity' ? (
          <div className="grid-2">
            <div className="card stack">
              <div className="eyebrow">Cantidad de producto trabajada</div>
              <p className="muted small" style={{ margin: 0 }}>
                {reservations
                  ? 'Con reservas activas, el sistema descuenta de la ubicación de la OF los componentes proporcionales a esta cantidad.'
                  : 'Sirve para el control de avance de la fase.'}
              </p>
              <QuantityPad value={quantity} onChange={setQuantity} unit="ud" />
            </div>
            <div className="card stack">
              {captureScrap ? (
                <>
                  <div className="eyebrow">Rechazo en esta fase</div>
                  <p className="muted small" style={{ margin: 0 }}>Déjalo a cero si no ha habido piezas defectuosas.</p>
                  <QuantityPad value={scrap} onChange={setScrap} unit="ud" />
                </>
              ) : null}
              <div className="spacer" />
              <button
                className="btn btn--primary btn--lg btn--block"
                disabled={reservations && parseQty(quantity) <= 0}
                onClick={() => void goToComponents()}
              >
                Continuar a consumos →
              </button>
            </div>
          </div>
        ) : null}

        {step === 'components' ? (
          loading ? (
            <Empty icon="⏳" title="Cargando componentes…" />
          ) : (
            <div className="stack">
              {components.length === 0 ? (
                <Empty
                  icon="✅"
                  title="Esta fase no tiene consumos manuales"
                  body="Solo se emitirá el tiempo de mano de obra de los operarios."
                />
              ) : (
                <div className="card">
                  <div className="card__head">
                    <div className="h2">Consumos de material</div>
                    <div className="spacer" />
                    <span className="row small muted">
                      <Dot tone="muted" /> No aplica
                      <Dot tone="danger" /> Falta detalle
                      <Dot tone="ok" /> Completo
                    </span>
                  </div>
                  <div className="list">
                    {components.map((component) => (
                      <div key={component.lineNum} className="list-item" style={{ cursor: 'default' }}>
                        <Dot
                          tone={
                            component.allocationStatus === 'complete'
                              ? 'ok'
                              : component.allocationStatus === 'pending'
                                ? 'danger'
                                : 'muted'
                          }
                        />
                        <div className="list-item__main">
                          <div className="list-item__title">{component.description}</div>
                          <div className="list-item__sub">
                            {component.itemCode} · almacén {component.warehouse}
                            {component.managedBy !== 'none' ? ` · por ${component.managedBy === 'batch' ? 'lotes' : 'nº de serie'}` : ''}
                          </div>
                        </div>
                        <input
                          className="input input--qty"
                          style={{ width: 150 }}
                          inputMode="decimal"
                          value={String(component.quantity)}
                          disabled={reservations}
                          onChange={(e) => updateComponent(component.lineNum, { quantity: parseQty(e.target.value), allocations: [] })}
                        />
                        <span className="muted small" style={{ width: 40 }}>{component.uom ?? ''}</span>
                        {component.managedBy !== 'none' || component.binEnabled ? (
                          <button className="btn" onClick={() => setEditing(component)}>
                            Lote / ubicación
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="row">
                <button className="btn" onClick={back}>
                  ← Atrás
                </button>
                <div className="spacer" />
                {blocking ? <Pill tone="danger">Falta asignar {blocking.itemCode}</Pill> : null}
                <button
                  className="btn btn--ok btn--lg"
                  disabled={busy || Boolean(blocking)}
                  onClick={() => void submit()}
                >
                  Finalizar fase
                </button>
              </div>

              {state && !state.employee.supervisor ? (
                <p className="muted small">
                  No tienes el rol de jefe de producción: al confirmar se pedirá el PIN de quien pueda autorizarlo.
                </p>
              ) : null}
            </div>
          )
        ) : null}
      </div>

      {editing ? (
        <AllocationDialog
          component={editing}
          onClose={() => setEditing(null)}
          onSave={(allocations) => {
            updateComponent(editing.lineNum, { allocations });
            setEditing(null);
          }}
        />
      ) : null}

      {askPin ? (
        <SupervisorPin
          action={`Finalizar la fase ${phaseCode} de la OF ${order?.docNum}`}
          onClose={() => setAskPin(false)}
          onConfirm={(pin) => {
            setAskPin(false);
            void submit(pin);
          }}
        />
      ) : null}
    </div>
  );
}

/** Reparto de la cantidad entre lotes, series y ubicaciones disponibles. */
function AllocationDialog({
  component,
  onSave,
  onClose,
}: {
  component: ComponentToIssue;
  onSave: (allocations: Allocation[]) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<(Allocation & { available: number })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (component.managedBy === 'none') {
      setRows(component.allocations.map((a) => ({ ...a, available: a.available ?? a.quantity })));
      setLoading(false);
      return;
    }
    api
      .availability(component.itemCode, component.warehouse ?? '', component.managedBy)
      .then((res) => {
        const assigned = new Map(component.allocations.map((a) => [`${a.code}|${a.binAbsEntry ?? ''}`, a.quantity]));
        setRows(
          res.availability.map((row) => ({
            code: row.code,
            binAbsEntry: row.binAbsEntry,
            binCode: row.binCode,
            available: row.available,
            quantity: assigned.get(`${row.code}|${row.binAbsEntry ?? ''}`) ?? 0,
          })),
        );
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [component]);

  const assigned = rows.reduce((sum, r) => sum + r.quantity, 0);
  const remaining = component.quantity - assigned;

  /** Reparto automático por orden de disponibilidad: lo habitual es aceptarlo. */
  const autoFill = () => {
    let left = component.quantity;
    setRows((current) =>
      current.map((row) => {
        const take = Math.min(left, row.available);
        left -= take;
        return { ...row, quantity: take };
      }),
    );
  };

  return (
    <Dialog
      title={`${component.description}`}
      subtitle={`${component.itemCode} · asignar ${qty(component.quantity)} ${component.uom ?? ''}`}
      onClose={onClose}
      wide
    >
      {loading ? (
        <Empty icon="⏳" title="Consultando disponibilidad…" />
      ) : rows.length === 0 ? (
        <Empty icon="📦" title="Sin existencias disponibles" body="No hay stock con lote, serie o ubicación para este artículo en el almacén." />
      ) : (
        <div className="list" style={{ maxHeight: '46vh', overflowY: 'auto' }}>
          {rows.map((row, index) => (
            <div key={`${row.code}-${row.binAbsEntry}-${index}`} className="list-item" style={{ cursor: 'default' }}>
              <div className="list-item__main">
                <div className="list-item__title">{row.code || 'Sin lote'}</div>
                <div className="list-item__sub">
                  {row.binCode ? `Ubicación ${row.binCode} · ` : ''}disponible {qty(row.available)}
                </div>
              </div>
              <input
                className="input input--qty"
                style={{ width: 150 }}
                inputMode="decimal"
                value={String(row.quantity)}
                onChange={(e) => {
                  const value = Math.min(parseQty(e.target.value), row.available);
                  setRows((current) => current.map((r, i) => (i === index ? { ...r, quantity: value } : r)));
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="row" style={{ marginTop: 20 }}>
        <Field label="Pendiente de asignar">
          <span className={remaining > 1e-6 ? 'h2' : 'h2'} style={{ color: remaining > 1e-6 ? 'var(--danger)' : 'var(--ok)' }}>
            {qty(Math.max(0, remaining))} {component.uom ?? ''}
          </span>
        </Field>
        <div className="spacer" />
        <button className="btn" onClick={autoFill}>
          Repartir automáticamente
        </button>
        <button className="btn" onClick={onClose}>
          Cancelar
        </button>
        <button
          className="btn btn--primary btn--lg"
          onClick={() => onSave(rows.filter((r) => r.quantity > 0).map(({ available: _available, ...rest }) => rest))}
        >
          Aceptar
        </button>
      </div>
    </Dialog>
  );
}
